import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import GroupsIcon from '@mui/icons-material/Groups';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const METRIC_LABEL = {
  anganwadi: 'Anganwadi Registered',
  supplementary: 'Supplementary Marked Not Applicable',
  thr: 'THR Marked Not Applicable',
};

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function SundargarhAnganwadi({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, metric: null, records: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/anganwadi/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh anganwadi data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalForms = summary?.totalForms ?? rows.reduce((s, r) => s + (Number(r.totalForms) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Forms" value={totalForms} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile label="Anganwadi Registered" value={`${summary?.anganwadiRegistered?.pct ?? 0}%`} icon={ChildCareIcon} accent="var(--info)" sub={`${summary?.anganwadiRegistered?.count ?? 0} of ${totalForms} forms`} />
      <StatTile label="Supplementary Marked N/A" value={`${summary?.supplementaryNA?.pct ?? 0}%`} icon={ChildCareIcon} accent={(summary?.supplementaryNA?.pct ?? 0) >= 25 ? 'var(--danger)' : 'var(--warning)'} sub="of Anganwadi-registered forms" />
      <StatTile label="THR Marked N/A" value={`${summary?.thrNA?.pct ?? 0}%`} icon={ChildCareIcon} accent={(summary?.thrNA?.pct ?? 0) >= 25 ? 'var(--danger)' : 'var(--warning)'} sub="of Anganwadi-registered forms" />
    </>
  );

  const openDrilldown = async (row, metric) => {
    setDrawer({ open: true, row, metric, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, metric, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/anganwadi/details?${params}`);
      setDrawer({ open: true, row, metric, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching anganwadi detail records:', err);
      setDrawer({ open: true, row, metric, records: [], loading: false });
    }
  };

  const metricCell = (row, dataKey, metricKey, highlight) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, metricKey); }}>
      {pctCell(row[dataKey], highlight)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalForms', label: 'Total Forms', align: 'center', render: (r) => <span className="cell-strong">{r.totalForms || 0}</span> },
    { key: 'anganwadiRegistered', label: 'Anganwadi Registered (% of Total)', align: 'center', render: (r) => metricCell(r, 'anganwadiRegistered', 'anganwadi', false) },
    { key: 'supplementaryNA', label: 'Supplementary Marked N/A (% of Registered)', align: 'center', render: (r) => metricCell(r, 'supplementaryNA', 'supplementary', true) },
    { key: 'thrNA', label: 'THR Marked N/A (% of Registered)', align: 'center', render: (r) => metricCell(r, 'thrNA', 'thr', true) },
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'anganwadiRegistration', label: 'Registered', render: (r) => r.anganwadiRegistration || '—' },
    { key: 'supplementaryNutritionReceived', label: 'Supplementary Nutrition', render: (r) => r.supplementaryNutritionReceived || '—' },
    { key: 'takeHomeRationReceived', label: 'Take-Home Ration', render: (r) => r.takeHomeRationReceived || '—' },
    { key: 'ulb', label: 'ULB', render: (r) => r.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.ward || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
  ];

  return (
    <RuleScreen
      title={rule?.title || 'Anganwadi Registered but Nutrition N/A'}
      subtitle="Among Anganwadi-registered households, how many have Supplementary Nutrition or Take-Home Ration marked 'Not applicable' — inconsistent with being registered."
      icon={ChildCareIcon}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}>
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar onApply={(v) => { setApplied(v); setPage(1); }} onClear={() => { setApplied(EMPTY); setPage(1); }} loading={loading} />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Anganwadi vs Nutrition Benefits by {tab.label.replace('By ', '')}</h3>
        <Pagination pagination={pagination} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} unitLabel="groups" shown={rows.length} />
        <DataTable columns={columns} rows={rows} loading={loading} emptyMessage='No data found. Adjust filters and click "Apply Filters".' getRowKey={(r, i) => `${r.name}-${i}`} />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${METRIC_LABEL[drawer.metric] || ''}` : ''}
        sections={
          drawer.row
            ? [{
                title: 'Breakdown',
                items: [
                  { label: 'Total Forms', value: drawer.row.totalForms || 0 },
                  { label: 'Anganwadi Registered', value: `${drawer.row.anganwadiRegistered?.count || 0} (${drawer.row.anganwadiRegistered?.pct || 0}%)` },
                  { label: 'Supplementary Marked N/A', value: `${drawer.row.supplementaryNA?.count || 0} (${drawer.row.supplementaryNA?.pct || 0}%)` },
                  { label: 'THR Marked N/A', value: `${drawer.row.thrNA?.count || 0} (${drawer.row.thrNA?.pct || 0}%)` },
                ],
              }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable columns={drawerRecordColumns} rows={drawer.records} loading={drawer.loading} emptyMessage="No households found." getRowKey={(r, i) => r.uuid || i} />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
